// @ts-expect-error - TS2614 - Module '"src/api-parsers"' has no exported member 'Workflow'. Did you mean to use 'import Workflow from "src/api-parsers"' instead? | TS2614 - Module '"src/api-parsers"' has no exported member 'Event'. Did you mean to use 'import Event from "src/api-parsers"' instead?
import type {Workflow, Event} from 'src/api-parsers';
import type {RouteLocation, Params} from 'src/types/router';

import * as React from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {Link} from 'src/rerouter';
import {useHistory} from 'src/rerouter/hooks';
import useDisplayConfig, {useJ2Enabled} from 'src/hooks/useDisplayConfig';
import {useApi} from 'src/hooks/useApi';
import {classify} from 'src/utils';
import {getSelectedKey, TRM_PATHS} from 'src/utils/app-shell';
import {
  useReleaseFlag,
  useAnalyticsV2,
  useTRMEnabled,
  useCrmRedesignedEnabled,
  useTrmAppShellEnabled,
  useCrmMailbox,
  useTalentPoolEnabled,
  useRecordsEnabled,
  useConversationTabEnabled,
  useAgentBuilderEnabled,
  useDiscoverActiveCandidateJobLogicViewEnabled,
  useDiscoverActiveJobsViewEnabled,
  useDataVariablePageV2Enabled,
} from 'src/hooks/product-flags';
import useAgencyConfig, {
  useDisableNewMessageCompose,
  useChatbotEnabled,
  useSchedulerEnabled,
  useJobMatchEnabledForAnyProduct,
  useSenseEvents,
  useVoiceAiMarketingPageEnabled,
  useAutomationSurvey,
} from 'src/hooks/useAgencyConfig';
import {useCRMMigrationEnabled, useTrmUserInfo} from 'src/hooks/useCrm';
import usePrivilege from 'src/hooks/usePrivilege';
import useReferrals from 'src/hooks/useReferrals';
import useI18n from 'src/hooks/useI18n';

import {getBaseInfo} from 'src/action-creators/crm';
import {getUnreadCounts} from 'src/action-creators/messages';
import {selectAgencyConfigFlag, selectAgencyConfig} from 'src/selectors/agency';

import {selectIsQueueBasedInbox} from 'src/selectors/messaging-queues';
import {selectTotalUnreadThreadCount} from 'src/selectors/messages';

import {SideMenuLink} from '@spaced-out/ui-design-system/lib/components/SideMenuLink';

import {SideMenuGroup} from 'src/components/app-shell/side-menu-group';

import {SideNavHeader} from 'src/components/app-shell/side-nav-header';
import {AccountBar} from 'src/components/app-shell/account-bar';

import {
  AnalyticsWorkflowList,
  DefaultWorkflowList,
} from 'src/components/navigation/workflow-detail';
import Loading from 'src/components/lib/loading/loading';
import TruncatedText from 'src/components/lib/truncated-text';
import ZendeskTrigger from 'src/components/lib/zendesk-trigger';
import {isInternalUser} from 'src/utils/check-internal-user';
import {
  ANALYTICS_API_V3,
  checkJdGeneratorEnabled,
  checkAnalyticsNewLandingPageEnabled,
} from 'src/utils/analytics-api-migration';
import {
  havePermissionForAppShell,
  CRM_PERMISSIONS,
  useAopEnabled,
} from 'src/utils/crm';

import ArrowLeft from 'src/images/arrow-left.svg';
import AnalyticsEngagementCohortsDetail from 'src/components/navigation/analytics-engagement-cohorts-detail';

import MetricsIcon from 'src/images/nav/metrics-icon.svg?noAttrs';
import ResponsesIcon from 'src/images/nav/responses-icon.svg';
import FlagIcon from 'src/images/flag-icon.svg?noAttrs';
import EngagementIcon from 'src/images/nav/engagement-icon.svg?noAttrs';
import ClicksIcon from 'src/images/nav/clicks-icon.svg?noAttrs';
import NpsIcon from 'src/images/nav/nps.svg?noAttrs';
import PerformanceIcon from 'src/images/nav/performance-icon.svg?noAttrs';
import JobMatchesIcon from 'src/images/nav/job_matches.svg?noAttrs';
import ChatbotAnalyticsIcon from 'src/images/nav/chatbot-analytics-icon.svg?noAttrs';
import ChatbotTranscriptsIcon from 'src/images/nav/chatbot-transcripts-icon.svg?noAttrs';
import VoicebotTranscriptsIcon from 'src/images/nav/voicebot-transcripts-icon.svg';
import MessagesAnalyticsIcon from 'src/images/nav/messages-analytics-icon.svg?noAttrs';

import DeliveryHistoryIcon from 'src/images/icons/delivery-history-icon.svg?noAttrs';

import AnalyticsReferralIcon from 'src/images/icons/referral-analytics-icon.svg';
import MeetingsAnalyticsIcon from 'src/images/icons/meetings-product.svg';

import css from './app-shell-side-nav.css';
import {hasArnieEntityCredGroup} from 'src/utils/data-integration.ts';
import {
  selectCredentials,
  selectCredentialsMetaData,
} from 'src/selectors/data-integration.ts';


type AppShellSideNavProps = {
  location: RouteLocation;
  workflows: {
    [id: string]: Workflow;
  };
  workflow?: Workflow;
  events: Event[] | null | undefined;
  curClient?: string;
  hidden: boolean;
  canViewAnalytics: boolean;
  canViewAudienceMembers: boolean;
  canViewWorkflows: boolean;
  canEditWorkflows: boolean;
  canEditSettings: boolean;
  canViewChat: boolean;
  canViewChatorEditChatbot: boolean;
  analyticsPerformancePageEnabled: boolean;
  analyticsCohortsEnabled: boolean;
  schedulingAllowed: boolean;
  schedulerEnabled: boolean;
  params: Params;
  canViewReferrals: boolean;
  canViewVoiceflow: boolean;
  expanded: boolean;
  setExpand: (arg1: boolean) => void;
  handleSenseUniversityOnClick: () => void;
  canViewSurveys: boolean;
  canEditSurveys: boolean;
};

export const AppShellSideNav = (props: AppShellSideNavProps) => {
  const {
    workflows,
    events,
    location,
    canViewSurveys,
    canViewAnalytics,
    canViewAudienceMembers,
    canViewWorkflows,
    canEditWorkflows,
    canEditSettings,
    canViewChat,
    canViewChatorEditChatbot,
    analyticsPerformancePageEnabled,
    analyticsCohortsEnabled,
    schedulingAllowed,
    schedulerEnabled,
    params,
    canViewReferrals,
    canViewVoiceflow,
    expanded,
    setExpand,
    handleSenseUniversityOnClick,
  } = props;
  const {pathname} = location;
  const atsBulkWritebackEnabled = useSelector((state) =>
    selectAgencyConfigFlag(state, 'atsBulkWritebackEnabled'),
  );
  const [showWorkflows, setShowWorkflows] = React.useState(false);
  const trmEnabled = useTRMEnabled();

  React.useEffect(() => {
    setShowWorkflows(false);
  }, [pathname]);

  const canNavigateToHomepage = useReleaseFlag('journeysHomepage');
  const handleWorkflowClick = React.useCallback(
    (event: React.SyntheticEvent) => {
      if (canNavigateToHomepage) {
        return;
      }
      event.preventDefault();
      setShowWorkflows(!showWorkflows);
    },
    [showWorkflows],
  );

  const handleAnalyticsClick = React.useCallback(
    (event: React.SyntheticEvent) => {
      const analyticsMatches = pathname.match(/^\/analytics$/);
      if (analyticsMatches) {
        event.preventDefault();
        setShowWorkflows(false);
      }
    },
    [pathname],
  );

  // TODO (kyle): figure out if all this matching is necessary
  let currentWorkflowId;

  const workflowMatches = pathname.match(/^\/journey\/(\d+)/);
  if (workflowMatches) {
    currentWorkflowId = workflowMatches && workflowMatches[1];
  }

  const analyticsMatches = pathname.match(/^\/analytics/);

  const analyticsWorkflowMatches = pathname.match(
    /^\/analytics\/journey\/(\d+)/,
  );
  if (analyticsWorkflowMatches) {
    currentWorkflowId = analyticsWorkflowMatches[1];
  }

  const hideSupportButton = ['survey', 'messages', 'contacts'].some((path) =>
    pathname.startsWith(`/${path}`),
  );

  const details = [];

  const customerBusinessType =
    useSelector(selectAgencyConfig).customerBusinessType;
  const isCorporate = customerBusinessType === 'corporate';

  if (canViewWorkflows && showWorkflows) {
    details.push(
      <DefaultWorkflowList
        key="workflows"
        workflows={workflows}
        canEdit={canEditWorkflows}
      />,
    );
  } else if (
    canViewAnalytics &&
    ((analyticsMatches && !checkAnalyticsNewLandingPageEnabled()) ||
      pathname === '/analytics' ||
      pathname.includes('/analytics/journey'))
  ) {
    details.push(
      // NOTE (kyle): to keep this component usefully pure, we only pass the pathname when it possibly matches one of its links
      <AnalyticsDetail
        key="analytics"
        workflows={workflows}
        location={location}
        analyticsPerformancePageEnabled={
          analyticsPerformancePageEnabled && !isCorporate
        }
      />,
    );
    if (currentWorkflowId && workflows[currentWorkflowId]) {
      details.push(
        <AnalyticsWorkflowDetail
          key="analytics-events"
          location={location}
          workflow={workflows[currentWorkflowId]}
          events={events}
        />,
      );
    }
    if (analyticsCohortsEnabled && pathname.startsWith('/analytics/cohorts')) {
      details.push(
        <AnalyticsEngagementCohortsDetail
          key="analytics-cohorts"
          // @ts-expect-error - TS2740 - Type 'RerouterLocation' is missing the following properties from type 'Location': ancestorOrigins, host, hostname, origin, and 5 more.
          location={location}
          params={params}
        />,
      );
    }
  }

  const shouldExpand = !(canViewChat && pathname.startsWith('/messages'));

  const showDetailsNav = !!details.length;

  return (
    <div className={css.container}>
      <SideNavBar
        onWorkflowClick={handleWorkflowClick}
        onAnalyticsClick={handleAnalyticsClick}
        pathname={pathname}
        showWorkflows={showWorkflows}
        canViewAnalytics={canViewAnalytics}
        canViewAudienceMembers={canViewAudienceMembers}
        canViewWorkflows={canViewWorkflows}
        trmEnabled={trmEnabled}
        canEditSettings={canEditSettings}
        canViewChat={canViewChat}
        canViewChatorEditChatbot={canViewChatorEditChatbot}
        // @ts-expect-error - TS2322 - Type 'number | boolean' is not assignable to type 'boolean'.
        atsBulkWritebackEnabled={atsBulkWritebackEnabled}
        schedulerEnabled={schedulerEnabled}
        schedulingAllowed={schedulingAllowed}
        canViewReferrals={canViewReferrals}
        canViewVoiceflow={canViewVoiceflow}
        shouldExpand={shouldExpand}
        expanded={expanded}
        setExpand={setExpand}
        handleSenseUniversityOnClick={handleSenseUniversityOnClick}
        canViewSurveys={canViewSurveys}
      />
      {!hideSupportButton && (
        // @ts-expect-error - TS2769 - No overload matches this call.
        <ZendeskTrigger className={css.zendeskSupportIcon} />
      )}
      {}
      {showDetailsNav && <div className={css.detailContainer}>{details}</div>}
    </div>
  );
};

const AnalyticsDetail = ({
  workflows,
  analyticsPerformancePageEnabled,
  location,
}: {
  workflows: {
    [id: string]: Workflow;
  };
  location: RouteLocation;
  analyticsPerformancePageEnabled: boolean;
}) => {
  const {pathname} = location;
  const {labelI18nInstance} = useI18n();
  const t = labelI18nInstance.t;
  const isChatbotEnabled = useSelector(selectAgencyConfig).enableChatbot;
  // TODO(darsh): once centralization is done ask analytics team to remove voicebot transcripts from API
  const showVoicebotTranscripts =
    useSelector(selectAgencyConfig).voiceAiEnabled;
  const [referralsEnabled, canUseReferral] = useReferrals();
  const analyticsV2Enabled = useAnalyticsV2();
  const schedulerEnabled = useSchedulerEnabled();
  const isDiscoverViewEnabled = useReleaseFlag('discoverViewEnabled');
  const discoverViewEnabled = isInternalUser() || isDiscoverViewEnabled;
  const jobMatchEnabled = useJobMatchEnabledForAnyProduct();
  // [TODO]: Remove this once we have a better way to handle this - One way is useDisplayConfig
  // Discussed with @dhirah [ Analytics ]
  const checkAccessForEmbedding = useApi<boolean>('/sigma/access', {
    apiPath: ANALYTICS_API_V3,
  });

  const isNewAnalyticsJourneyDashboard =
    pathname === '/analytics' && checkAnalyticsNewLandingPageEnabled();

  const analyticsWorkflowList = (
    <AnalyticsWorkflowList key="analytics" workflows={workflows} isAnalytics />
  );

  return (
    <div className={css.detail}>
      <section className={css.detailSection}>
        <div className={css.detailTitle}>{t('Dashboards', 'Dashboards')}</div>
        <div className={css.detailItems}>
          {analyticsPerformancePageEnabled && !isNewAnalyticsJourneyDashboard && (
            <AnalyticsDetailItem
              icon={<PerformanceIcon />}
              to="/analytics/performance"
              data-qa-id="select-performance-analytics"
            >
              {t('Performance', 'Performance')}
            </AnalyticsDetailItem>
          )}
          <AnalyticsDetailItem
            icon={<MetricsIcon />}
            to="/analytics"
            onlyActiveOnIndex
            data-qa-id="select-overall-analytics"
          >
            {analyticsV2Enabled
              ? t('Overview_Metrics', 'Overview Metrics')
              : t('Overall_Metrics', 'Overall Metrics')}
          </AnalyticsDetailItem>
          {!isNewAnalyticsJourneyDashboard && (
            <AnalyticsDetailItem
              icon={<ResponsesIcon />}
              to="/analytics/responses"
              data-qa-id="select-responses-analytics"
            >
              {t('Responses', 'Responses')}
            </AnalyticsDetailItem>
          )}
          {!isNewAnalyticsJourneyDashboard && (
            <AnalyticsDetailItem
              icon={<FlagIcon />}
              to="/analytics/flagged-responses"
              data-qa-id="select-flagged-analytics"
            >
              {t('Flagged_Responses', 'Flagged Responses')}
            </AnalyticsDetailItem>
          )}
          {analyticsV2Enabled && !isNewAnalyticsJourneyDashboard && (
            <AnalyticsDetailItem
              // @ts-expect-error - TS2322 - Type '{ className: string; }' is not assignable to type 'IntrinsicAttributes'.
              icon={<DeliveryHistoryIcon className={css.deliveryHistory} />}
              to="/analytics/delivery-history"
            >
              {t('Delivery_History', 'Delivery History')}
            </AnalyticsDetailItem>
          )}
          {!isNewAnalyticsJourneyDashboard && (
            <AnalyticsDetailItem
              icon={<ClicksIcon />}
              to="/analytics/clicks"
              data-qa-id="select-clicks-analytics"
            >
              {t('External_Link_Clicks', 'External Link Clicks')}
            </AnalyticsDetailItem>
          )}
          {isChatbotEnabled && !isNewAnalyticsJourneyDashboard && (
            <>
              <AnalyticsDetailItem
                icon={<ChatbotAnalyticsIcon />}
                to="/analytics/chatbot"
                data-qa-id="select-chatbot-analytics"
              >
                {t('Chatbot_Metrics', 'Chatbot Metrics')}
              </AnalyticsDetailItem>
              <AnalyticsDetailItem
                icon={<ChatbotTranscriptsIcon />}
                to="/analytics/transcripts"
                data-qa-id="select-chatbot-transcripts"
              >
                {t('Chatbot_Transcripts', 'Chatbot Transcripts')}
              </AnalyticsDetailItem>
            </>
          )}
          {showVoicebotTranscripts && !isNewAnalyticsJourneyDashboard && (
            <AnalyticsDetailItem
              icon={<VoicebotTranscriptsIcon />}
              to="/analytics/voicebot-transcripts"
              data-qa-id="select-voicebot-transcripts"
            >
              {t('Voicebot_Transcripts', 'Voicebot Transcripts')}
            </AnalyticsDetailItem>
          )}
          {!isNewAnalyticsJourneyDashboard && (
            <AnalyticsDetailItem
              icon={<MessagesAnalyticsIcon />}
              to="/analytics/messages/outbound"
              data-qa-id="select-messages-analytics"
            >
              {t('Messages', 'Messages')}
            </AnalyticsDetailItem>
          )}
          {referralsEnabled &&
            canUseReferral &&
            !isNewAnalyticsJourneyDashboard && (
              <AnalyticsDetailItem
                icon={<AnalyticsReferralIcon />}
                to="/analytics/referral"
                data-qa-id="select-referral-analytics"
              >
                {t('Referrals', 'Referrals')}
              </AnalyticsDetailItem>
            )}
          {schedulerEnabled && !isNewAnalyticsJourneyDashboard && (
            <AnalyticsDetailItem
              icon={<MeetingsAnalyticsIcon />}
              to="/analytics/meetings"
            >
              {t('Meetings', 'Meetings')}
            </AnalyticsDetailItem>
          )}
          {!isNewAnalyticsJourneyDashboard && (
            <AnalyticsDetailItem
              icon={<NpsIcon />}
              to="/analytics/nps"
              data-qa-id="select-nps-analytics"
            >
              {t('NPS', 'NPS')}
            </AnalyticsDetailItem>
          )}
          {jobMatchEnabled &&
            discoverViewEnabled &&
            !isNewAnalyticsJourneyDashboard && (
              <AnalyticsDetailItem
                icon={<JobMatchesIcon />}
                to="/analytics/discover"
                data-qa-id="select-job-match-analytics"
              >
                {t('Job_Matches', 'Job Matches')}
              </AnalyticsDetailItem>
            )}
          {referralsEnabled &&
            canUseReferral &&
            !isNewAnalyticsJourneyDashboard && (
              <AnalyticsDetailItem
                icon={<AnalyticsReferralIcon />}
                to="/analytics/referral"
                data-qa-id="select-referral-analytics"
              >
                {t('Referrals', 'Referrals')}
              </AnalyticsDetailItem>
            )}
        </div>
      </section>
      {checkAnalyticsNewLandingPageEnabled()
        ? isNewAnalyticsJourneyDashboard
          ? analyticsWorkflowList
          : null
        : analyticsWorkflowList}
    </div>
  );
};

const AnalyticsWorkflowDetail = ({
  location,
  workflow,
  events,
}: {
  location: RouteLocation;
  workflow: Workflow;
  events: Event[] | null | undefined;
}) => {
  const {labelI18nInstance} = useI18n();
  const t = labelI18nInstance.t;
  const analyticsV2Enabled = useAnalyticsV2();
  const isChatbotEnabled = useSelector(selectAgencyConfig).enableChatbot;
  // TODO(darsh): once centralization is done ask analytics team to remove voicebot transcripts from API
  const showVoicebotTranscripts =
    useSelector(selectAgencyConfig).voiceAiEnabled;
  const basePath = '/analytics/journey/' + workflow.id;

  return (
    <div className={css.detail}>
      <Link
        className={css.detailWorkflowTitle}
        to={{
          ...location,
          pathname: `/analytics`,
        }}
      >
        <ArrowLeft className={css.backArrow} />
        <TruncatedText text={workflow.name} limit={25} />
      </Link>

      <section className={css.detailSectionEvents}>
        <div className={css.detailTitleEvents}>
          {t('Dashboards', 'Dashboards')}
        </div>
        <div className={css.detailItems}>
          <Link
            className={css.detailItemEvents}
            activeClassName={css.detailEventOn}
            onlyActiveOnIndex
            to={{
              ...location,
              pathname: basePath,
            }}
          >
            {/* @ts-expect-error - TS2322 - Type '{ className: string; }' is not assignable to type 'IntrinsicAttributes'. */}
            <MetricsIcon className={css.metricsIcon} />
            {analyticsV2Enabled
              ? t('Overview_Metrics', 'Overview Metrics')
              : t('Metrics', 'Metrics')}
          </Link>

          <EventDetailItem
            icon={<ResponsesIcon />}
            to={basePath + '/responses'}
          >
            {t('Responses', 'Responses')}
          </EventDetailItem>

          <EventDetailItem
            icon={<FlagIcon />}
            to={basePath + '/flagged-responses'}
          >
            {t('Flagged_Responses', 'Flagged Responses')}
          </EventDetailItem>

          {analyticsV2Enabled && (
            <EventDetailItem
              // @ts-expect-error - TS2322 - Type '{ className: string; }' is not assignable to type 'IntrinsicAttributes'.
              icon={<DeliveryHistoryIcon className={css.deliveryHistory} />}
              to={`${basePath}/delivery-history`}
            >
              {t('Delivery_History', 'Delivery History')}
            </EventDetailItem>
          )}

          <EventDetailItem icon={<ClicksIcon />} to={basePath + '/clicks'}>
            {t('Clicks', 'Clicks')}
          </EventDetailItem>

          {isChatbotEnabled && (
            <>
              <EventDetailItem
                icon={<ChatbotAnalyticsIcon />}
                to={basePath + '/chatbot'}
              >
                {t('Chatbot_Metrics', 'Chatbot Metrics')}
              </EventDetailItem>
              <EventDetailItem
                icon={<ChatbotTranscriptsIcon />}
                to={basePath + '/transcripts'}
              >
                {t('Chatbot_Transcripts', 'Chatbot Transcripts')}
              </EventDetailItem>
            </>
          )}
          {showVoicebotTranscripts && (
            <EventDetailItem
              icon={<VoicebotTranscriptsIcon />}
              to={basePath + '/voicebot-transcripts'}
            >
              {t('Voicebot_Transcripts', 'Voicebot Transcripts')}
            </EventDetailItem>
          )}

          <EventDetailItem
            icon={<EngagementIcon />}
            to={basePath + '/engagement'}
          >
            {t('Engagement', 'Engagement')}
          </EventDetailItem>
        </div>
      </section>

      <section className={css.detailSectionEvents}>
        <div className={css.detailTitleEvents}>
          {t('Touchpoints', 'Touchpoints')}
        </div>
        <div className={css.detailEvents}>
          {events ? (
            events.map((event) => (
              <TruncatedText
                text={event.title}
                limit={25}
                component={Link}
                className={css.detailEvent}
                activeClassName={css.detailEventOn}
                to={{
                  ...location,
                  pathname: `${basePath}/touchpoint/${event.id}`,
                }}
                key={event.id}
              />
            ))
          ) : (
            <Loading />
          )}
        </div>
      </section>
    </div>
  );
};

function DetailItem({
  children,
  className,
  activeClassName = css.detailItemOn,
  icon,
  ...props
}: React.ComponentProps<typeof Link> & {
  icon: React.ReactNode;
}) {
  return (
    <Link
      className={className || css.detailItem}
      activeClassName={activeClassName}
      {...props}
    >
      <div className={css.detailIcon}>{icon}</div>
      {children}
    </Link>
  );
}

// @ts-expect-error - TS7031 - Binding element 'to' implicitly has an 'any' type.
function AnalyticsDetailItem({to, ...props}) {
  return (
    // @ts-expect-error - TS2741 - Property 'icon' is missing in type '{ to: (location: RerouterLocation) => { pathname: any; href: string; search: string; searchParams: URLSearchParams; query: Query; hash: string; state: unknown; }; }' but required in type '{ icon: ReactNode; }'.
    <DetailItem {...props} to={(location) => ({...location, pathname: to})} />
  );
}

// @ts-expect-error - TS7006 - Parameter 'props' implicitly has an 'any' type.
function EventDetailItem(props) {
  return (
    <AnalyticsDetailItem
      {...props}
      className={css.detailItemEvents}
      activeClassName={css.detailEventOn}
    />
  );
}

function SideNavBar(props: {
  onWorkflowClick: (arg1: React.SyntheticEvent) => unknown;
  onAnalyticsClick: (arg1: React.SyntheticEvent) => unknown;
  expand?: unknown;
  // NOTE (kyle): below props needed for lifecylce hooks
  pathname: string;
  showWorkflows?: unknown;
  canViewAnalytics: boolean;
  canViewAudienceMembers: boolean;
  canViewSurveys: boolean;
  canViewWorkflows: boolean;
  trmEnabled: boolean;
  canEditSettings: boolean;
  canViewChat: boolean;
  canViewChatorEditChatbot: boolean;
  atsBulkWritebackEnabled: boolean;
  schedulerEnabled: boolean;
  schedulingAllowed: boolean;
  canViewReferrals: boolean;
  canViewVoiceflow: boolean;
  shouldExpand: boolean;
  setExpand: (arg1: boolean) => void;
  expanded: boolean;
  handleSenseUniversityOnClick: () => void;
}) {
  const {
    canViewSurveys,
    onWorkflowClick,
    onAnalyticsClick,
    canViewAnalytics,
    canViewAudienceMembers,
    canViewWorkflows,
    trmEnabled,
    canEditSettings,
    canViewChat,
    canViewChatorEditChatbot,
    atsBulkWritebackEnabled,
    schedulerEnabled,
    schedulingAllowed,
    canViewReferrals,
    canViewVoiceflow,
    setExpand,
    expanded,
    handleSenseUniversityOnClick,
  } = props;

  const dispatch = useDispatch();
  const history = useHistory();
  const {location} = history;

  const isTRMCustomer = useDisplayConfig('is_trm_customer');
  const isTrmAppShellEnabled = useTrmAppShellEnabled();

  const msgUnreadCount = useSelector(selectTotalUnreadThreadCount);
  const trmUserInfo = useTrmUserInfo();
  const showUnreadCountBadge = msgUnreadCount > 0;

  React.useEffect(() => {
    dispatch(getUnreadCounts());

    // TRM api for permissions
    if (isTrmAppShellEnabled && !trmUserInfo) {
      dispatch(getBaseInfo());
    }
  }, []);
  const disableSetOfMessagingProductsForGiGroup = useDisableNewMessageCompose();
  const isChatbotEnabled = useChatbotEnabled();
  const recordsEnabled = useRecordsEnabled();
  const isAgentBuilderEnabled = useAgentBuilderEnabled();
  const showSenseEvents = useSenseEvents();
  const conversationTabEnabled = useConversationTabEnabled();
  const isChatbotAllowed = usePrivilege('EDIT_CHATBOT');
  const isVoiceAllowed = usePrivilege('USE_VOICEFLOW');
  const voiceEnabledAgentBuilder = useReleaseFlag('voiceEnabledAgentBuilder');
  const voiceAiV2Enabled = useAgencyConfig('voiceAiV2Enabled');
  const voiceAiEnabled = useAgencyConfig('voiceAiEnabled');
  const [referralsEnabled, canUseReferral] = useReferrals();
  const referralV2 = useAgencyConfig('enableReferralV2');
  const aiRecruiterEnabled = useAgencyConfig('aiRecruiterEnabled');
  const analyticsDefaultPageEnabled = useReleaseFlag(
    'analyticsDefaultPageEnabled',
  );
  const discoverActiveCandidateJobLogicViewEnabled =
    useDiscoverActiveCandidateJobLogicViewEnabled();

  const {labelI18nInstance} = useI18n();
  const t = labelI18nInstance.t;
  const landingPageView = useDisplayConfig('landing_page_view');
  const jobsListEnabled = useDisplayConfig('jobs_list_enabled');
  const canViewAutomationWorkflows = useDisplayConfig('aep_workflow_view');
  const j2Enabled = useJ2Enabled();
  const automationSurveyEnabled = useAutomationSurvey();
  const showVoiceFlowMarketingLandingPage = useVoiceAiMarketingPageEnabled();
  const isDiscoverActiveJobsViewEnabled = useDiscoverActiveJobsViewEnabled();
  const canViewListsBtn = useDisplayConfig('show_audience_list_nav');
  const canViewFieldsPage =
    useDataVariablePageV2Enabled() && !isTRMCustomer && canEditSettings;

  const customerBusinessType =
    useSelector(selectAgencyConfig).customerBusinessType;
  const isCorporate = customerBusinessType === 'corporate';

  const viewJobsPrivilege = usePrivilege('VIEW_JOBS');
  const adminJobsPrivilege = usePrivilege('ADMIN_JOBS');
  const isPageHaveViewPrivilege = usePrivilege('VIEW_PAGES');
  const isPageHaveAdminPrivilege = usePrivilege('ADMIN_PAGES');
  const isPageHaveEditPrivilege = usePrivilege('EDIT_PAGES');
  const isHVBEnabledForMSG = useReleaseFlag('messagingBroadcastV2');
  const hasQueueInbox = useSelector(selectIsQueueBasedInbox);
  const leadManagementEnabled = useDisplayConfig('lead_management_enabled');
  const isLeadsHaveViewPrivilege = usePrivilege('VIEW_LEAD_MANAGEMENT');
  const isLeadsHaveAdminPrivilege = usePrivilege('ADMIN_LEAD_MANAGEMENT');
  const isLeadsHaveEditPrivilege = usePrivilege('EDIT_LEAD_MANAGEMENT');
  const viewRecordsPrivilege = usePrivilege('VIEW_RECORDS');
  const viewAiRecruiter = usePrivilege('VIEW_AI_RECRUITER');
  const viewCredentialsPrivilege = usePrivilege('VIEW_CREDENTIALS');
  const editCredentialsPrivilege = usePrivilege('EDIT_CREDENTIALS');
  const credentialsUIEnabled = useReleaseFlag('dataCredentialsUiEnabled');

  const credentialMetaData = useSelector(selectCredentialsMetaData);
  const credentials = useSelector(selectCredentials);
  const hasArnieEntityCredGroupInMeta =
    (credentialMetaData && hasArnieEntityCredGroup(credentialMetaData)) ||
    false;
  const hasArnieEntityCredGroupInCredentials =
    (credentials && hasArnieEntityCredGroup(credentials)) || false;
  const showDataIntegrationTab = hasArnieEntityCredGroupInMeta
    ? hasArnieEntityCredGroupInCredentials
    : true;

  const isNewCrmUiEnabled = useCrmRedesignedEnabled();
  const canUseCrmMailbox = useCrmMailbox();
  const crmMigrationEnabled = useCRMMigrationEnabled();
  const isAopEnabled = useAopEnabled();
  const talentPoolEnabled = useTalentPoolEnabled();

  const isShowRecords = recordsEnabled && viewRecordsPrivilege;
  const isDataIntegrationEnabled =
    (viewCredentialsPrivilege || editCredentialsPrivilege) &&
    credentialsUIEnabled &&
    showDataIntegrationTab;

  const leadsHaveNoAccess =
    isLeadsHaveViewPrivilege ||
    isLeadsHaveAdminPrivilege ||
    isLeadsHaveEditPrivilege;

  const pagessHaveNoAccess =
    isPageHaveViewPrivilege ||
    isPageHaveAdminPrivilege ||
    isPageHaveEditPrivilege;
  const careerSiteEnabled = useDisplayConfig('career_site_enabled');

  const isTrmRoute = isTrmAppShellEnabled && location.pathname.includes(`trm`);
  const selectedKey = getSelectedKey(
    location.pathname,
    location.href,
    isTrmRoute,
  );

  //const selectedKey = getSelectedSideNav();

  const handleSideMenuChange = (
    e: React.SyntheticEvent<HTMLElement> | Record<any, any>,
    pageNameKey = '',
  ) => {
    if (pageNameKey === 'dashboard') {
      history.push('/dashboard');
    } else if (pageNameKey === 'jobs.active') {
      history.push('/jobs/recruiter-hub');
    } else if (pageNameKey === 'jobs.lists') {
      history.push('/jobs/lists');
    } else if (pageNameKey === 'journeys') {
      history.push('/journeys');
      // Note: Just carrying over existing feature. Don't know why it is here.
      // @ts-expect-error - TS2345 - Argument of type 'Record<any, any> | SyntheticEvent<HTMLElement, Event>' is not assignable to parameter of type 'SyntheticEvent<Element, Event>'.
      onWorkflowClick(e);
    } else if (pageNameKey === 'voiceflow') {
      history.push(
        showVoiceFlowMarketingLandingPage
          ? '/voiceflow/landing-page'
          : '/voiceflow',
      );
    } else if (pageNameKey === 'analytics') {
      // @ts-expect-error - TS2345 - Argument of type 'Record<any, any> | SyntheticEvent<HTMLElement, Event>' is not assignable to parameter of type 'SyntheticEvent<Element, Event>'.
      onAnalyticsClick(e);
      if (checkAnalyticsNewLandingPageEnabled()) {
        history.push('/reports');
      } else if (!isCorporate && analyticsDefaultPageEnabled) {
        history.push('/analytics/performance');
      } else if (!canViewAnalytics && canViewChat) {
        history.push('/analytics/messages/outbound');
      } else {
        history.push('/analytics');
      }
    } else if (pageNameKey === 'configuration') {
      if (isTRMCustomer) {
        history.push('/settings/accounts');
      } else if (canEditSettings) {
        history.push('/settings');
      } else {
        history.push('/settings/reports');
      }
    } else if (pageNameKey === 'referral' && !!referralV2 && canViewReferrals) {
      history.push('/referralv2');
    } else if (Object.keys(TRM_PATHS).includes(pageNameKey)) {
      // @ts-expect-error - TS7053 - Element implicitly has an 'any' type because expression of type 'string' can't be used to index type '{ readonly 'jobs.onboarding': "/trm#/onboarding"; readonly 'jobs.hiringplan': "/trm#/settings/hiring-plan-setup/hiring-plans"; readonly 'people.trm-candidates': "/trm#/candidates"; ... 6 more ...; readonly 'jobs.trm': "/trm#/jobs"; }'.
      history.push(TRM_PATHS[pageNameKey]);
    } else if (pageNameKey === 'jobs.crm') {
      history.push(`/crm/jobs`);
    } else if (pageNameKey.includes('.')) {
      const path = pageNameKey.replace(/\./g, '/');
      history.push(`/${path}`);
    } else if (pageNameKey === 'automation-surveys') {
      history.push('/automation-surveys');
    } else if (pageNameKey === 'ai-recruiter') {
      history.push('/ai-recruiter');
    } else {
      history.push(`/${pageNameKey}`);
    }
  };

  const pageNameKeyToPathnameMap = {
    dashboard: '/dashboard',
    journeys: '/journeys',
    analytics: checkAnalyticsNewLandingPageEnabled()
      ? '/reports'
      : !isCorporate && analyticsDefaultPageEnabled
      ? '/analytics/performance'
      : !canViewAnalytics && canViewChat
      ? '/analytics/messages/outbound'
      : '/analytics',
    configuration: '/settings',
    referral: '/referralv2',
    voiceflow: showVoiceFlowMarketingLandingPage
      ? '/voiceflow/landing-page'
      : '/voiceflow',
    'jobs.crm': '/crm/jobs',
    'jobs.active': '/jobs/recruiter-hub',
  } as const;

  const getPathFromPageNameKey = (pageNameKey: string) => {
    if (pageNameKey in pageNameKeyToPathnameMap) {
      // @ts-expect-error - TS7053 - Element implicitly has an 'any' type because expression of type 'string' can't be used to index type '{ readonly dashboard: "/dashboard"; readonly journeys: "/journeys"; readonly analytics: "/analytics" | "/reports" | "/analytics/messages/outbound" | "/analytics/performance"; readonly configuration: "/settings"; readonly referral: "/referralv2"; readonly voiceflow: "/voiceflow/landing-page" | "/voiceflow"; }'.
      return pageNameKeyToPathnameMap[pageNameKey];
    } else if (pageNameKey.includes('.')) {
      return `/${pageNameKey.replace(/\./g, '/')}`;
    } else {
      return `/${pageNameKey}`;
    }
  };

  const handleSubLinkSelect = (key: string) => {
    handleSideMenuChange({}, key);
  };

  const automationSubMenus = [
    ...(canViewWorkflows
      ? [
          {
            key: 'journeys',
            label: t('Journeys', 'Journeys'),
          },
        ]
      : []),
    ...(canViewAutomationWorkflows && j2Enabled
      ? [
          {
            key: 'automation-workflows',
            label: t('Workflows', 'Workflows'),
          },
        ]
      : []),
    ...(canViewVoiceflow || showVoiceFlowMarketingLandingPage
      ? [
          {
            key: 'voiceflow',
            label: t('Voiceflows', 'Voiceflows'),
            iconRight: showVoiceFlowMarketingLandingPage ? 'lock' : '',
          },
        ]
      : []),
    ...(schedulerEnabled && schedulingAllowed
      ? [
          {
            key: 'meetings',
            label: t('Scheduling', 'Scheduling'),
          },
        ]
      : []),
    ...(isChatbotEnabled && isChatbotAllowed
      ? [
          {
            key: 'conversations',
            label: t('Chatbots', 'Chatbots'),
          },
        ]
      : []),
    ...(canViewWorkflows && atsBulkWritebackEnabled
      ? [
          {
            key: 'database-cleanup',
            label: t('Database_Cleanup', 'Database Cleanup'),
          },
        ]
      : []),
    ...(automationSurveyEnabled && canViewSurveys
      ? [
          {
            key: 'automation-surveys',
            label: t('Surveys', 'Surveys'),
          },
        ]
      : []),
    ...(canViewAutomationWorkflows &&
    j2Enabled &&
    !!aiRecruiterEnabled &&
    viewAiRecruiter
      ? [
          {
            key: 'ai-recruiter',
            label: t('AI_Recruiter', 'AI Recruiter'),
          },
        ]
      : []),
    ...(isAgentBuilderEnabled &&
    ((isChatbotEnabled && isChatbotAllowed) ||
      (voiceEnabledAgentBuilder &&
        (voiceAiV2Enabled || voiceAiEnabled) &&
        isVoiceAllowed))
      ? [
          {
            key: 'agents',
            label: t('AI_Agents', 'AI Agents'),
          },
        ]
      : []),
  ];
  const dataManagementSubMenus = [
    ...(isDiscoverActiveJobsViewEnabled
      ? [
          {
            key: 'jobs.active',
            label: t('Recruiter_Hub', 'Recruiter Hub'),
          },
        ]
      : []),
    ...(canViewListsBtn
      ? [
          {
            key: 'jobs.lists',
            label: t('Job_Lists', 'Job Lists'),
          },
        ]
      : []),
    ...(isShowRecords
      ? [
          {
            key: 'people.candidates',
            label: t('All_Records', 'All Records'),
          },
        ]
      : []),
    ...(discoverActiveCandidateJobLogicViewEnabled
      ? [
          {
            key: 'people.candidates.active',
            label: t('Active_Candidates', 'Active Candidates'),
          },
        ]
      : []),
    ...(isDataIntegrationEnabled
      ? [
          {
            key: 'settings.data-integration',
            label: t('Data_Integration', 'Data Integration'),
          },
        ]
      : []),
    ...(conversationTabEnabled
      ? [
          {
            key: 'people.activity',
            label: t('Activity', 'Activity'),
          },
        ]
      : []),
    ...(canViewFieldsPage
      ? [
          {
            key: 'fields',
            label: t('Fields', 'Fields'),
          },
        ]
      : []),
  ].filter((item) => {
    if (item.key === 'jobs.active' || item.key === 'jobs.lists') {
      // [TRM] If the user has access to the CRM Jobs, we don't show the Sense Jobs page.
      if (
        isNewCrmUiEnabled &&
        trmEnabled &&
        havePermissionForAppShell(CRM_PERMISSIONS.VIEW_REQUISITION)
      ) {
        return false;
      }

      if (item.key === 'jobs.active') {
        return (
          isDiscoverActiveJobsViewEnabled &&
          (viewJobsPrivilege || adminJobsPrivilege)
        );
      }
      return (
        jobsListEnabled &&
        canViewListsBtn &&
        (viewJobsPrivilege || adminJobsPrivilege)
      );
    }
    return true;
  });

  const sourcingSubMenus = [
    // As per product, if Sense Referral is enabled, we don't show the TRM Referral
    ...(Boolean(referralV2) && canViewReferrals
      ? [
          {
            key: 'referral',
            label: t('Referrals', 'Referrals'),
          },
        ]
      : trmEnabled &&
        isTrmAppShellEnabled &&
        havePermissionForAppShell(CRM_PERMISSIONS.VIEW_EMPLOYEE_REFERRAL_PAGE)
      ? [
          {
            key: 'trm-referral',
            label: t('Referrals', 'Referrals'),
          },
        ]
      : []),
    ...(landingPageView && pagessHaveNoAccess
      ? [
          {
            key: 'pages',
            label: t('Pages', 'Pages'),
          },
          ...(careerSiteEnabled && pagessHaveNoAccess
            ? [
                {
                  key: 'pages.sites',
                  label: t('Career_Sites', 'Career Sites'),
                },
              ]
            : []),
        ]
      : []),
    //[TRM] As per product if both pages are enabled then show both so who ever is turning it on can see and take action accordingly
    ...(trmEnabled &&
    isTrmAppShellEnabled &&
    havePermissionForAppShell(CRM_PERMISSIONS.VIEW_CAREER_PAGE)
      ? [
          {
            key: 'trm-career-page',
            label: t('Careers_Page', 'Careers Page'),
          },
        ]
      : []),
    ...(showSenseEvents
      ? [
          {
            key: 'drive',
            label: t('Events', 'Events'),
          },
        ]
      : []),
  ];

  const jobsPermissionMap = {
    'jobs.onboarding': [CRM_PERMISSIONS.VIEW_ONBOARDING_TAB],
    'jobs.hiringplan': [
      CRM_PERMISSIONS.VIEW_SETTING,
      CRM_PERMISSIONS.VIEW_GLOBAL_HIRING_PLAN,
    ],
    'jobs.internal-posting': [CRM_PERMISSIONS.VIEW_IJP_REQUISITIONS],
  } as const;

  const dashboardPermissionMap = {
    'dashboard.overview_trm': [CRM_PERMISSIONS.VIEW_DASHBOARD],
    'dashboard.approvals_trm': [CRM_PERMISSIONS.VIEW_APPROVAL],
  } as const;

  const filteredJobsItems =
    [
      {key: 'jobs.crm', label: t('All_Jobs', 'All Jobs')},
      {key: 'jobs.trm', label: t('All_Jobs', 'All Jobs')},
      {key: 'jobs.hiringplan', label: t('Hiring_Plans', 'Hiring Plans')},
      {key: 'jobs.onboarding', label: t('Onboarding', 'Onboarding')},
      {
        key: 'jobs.internal-posting',
        label: t('Internal_Job_Portal', 'Internal Job Portal'),
      },
      ...(isAopEnabled &&
      havePermissionForAppShell(CRM_PERMISSIONS.VIEW_AOP) &&
      isTrmAppShellEnabled
        ? [
            {
              key: 'jobs.aop',
              label: t('Annual_Operating_Plan', 'Annual Operating Plan'),
            },
          ]
        : []),
    ].filter((item: {key: string; label: string}) => {
      // @ts-expect-error - TS7053 - Element implicitly has an 'any' type because expression of type 'string' can't be used to index type '{ readonly 'jobs.onboarding': readonly ["VIEW_ONBOARDING_TAB"]; readonly 'jobs.hiringplan': readonly ["VIEW_SETTING", "VIEW_GLOBAL_HIRING_PLAN"]; readonly 'jobs.internal-posting': readonly ["VIEW_IJP_REQUISITIONS"]; }'.
      const permissions = jobsPermissionMap[item.key];
      if (permissions) {
        return (
          isTrmAppShellEnabled &&
          trmEnabled &&
          // @ts-expect-error - TS7006 - Parameter 'permission' implicitly has an 'any' type.
          permissions.every((permission) =>
            havePermissionForAppShell(permission),
          )
        );
      }

      if (item.key === 'jobs.crm') {
        return (
          isNewCrmUiEnabled &&
          trmEnabled &&
          havePermissionForAppShell(CRM_PERMISSIONS.VIEW_REQUISITION)
        );
      }
      if (item.key === 'jobs.trm') {
        return !isNewCrmUiEnabled && trmEnabled && isTrmAppShellEnabled;
      }
      return true;
    }) || [];

  const filteredDashboardItems =
    [
      {
        key: 'dashboard.overview_trm',
        label: 'Overview',
      },
      {
        key: 'dashboard.approvals_trm',
        label: 'Approvals',
      },
    ].filter((item: {key: string; label: string}) => {
      // @ts-expect-error - TS7053 - Element implicitly has an 'any' type because expression of type 'string' can't be used to index type '{ readonly 'dashboard.overview_trm': readonly ["VIEW_DASHBOARD"]; readonly 'dashboard.approvals_trm': readonly ["VIEW_APPROVAL"]; }'.
      const permissions = dashboardPermissionMap[item.key];
      if (permissions) {
        return (
          isTrmAppShellEnabled &&
          permissions.every(
            // @ts-expect-error - TS7006 - Parameter 'permission' implicitly has an 'any' type.
            (permission) => havePermissionForAppShell(permission) && trmEnabled,
          )
        );
      }
      return true;
    }) || [];

  const filteredPeopleItems = [
    ...(!isShowRecords && canViewAudienceMembers
      ? [
          {
            key: 'people.candidates',
            label: t('All_People', 'All People'),
          },
        ]
      : []),
    ...(crmMigrationEnabled &&
    havePermissionForAppShell(CRM_PERMISSIONS.VIEW_CANDIDATE)
      ? [
          {
            key: 'crm.candidates',
            label: t('Candidates', 'Candidates'),
          },
        ]
      : [
          {
            key: 'people.trm-candidates',
            label: t('Candidates', 'Candidates'),
          },
        ]),
    ...(talentPoolEnabled
      ? [
          {
            key: 'talent-pools',
            label: t('Talent_Pools', 'Talent Pools'),
          },
        ]
      : []),
    ...(canViewAudienceMembers
      ? [
          {
            key: 'people.lists',
            label: t('Lists', 'Lists'),
          },
        ]
      : []),
    ...(leadManagementEnabled && leadsHaveNoAccess && canViewAudienceMembers
      ? [
          {
            key: 'people.leads',
            label: t('Leads', 'Leads'),
          },
        ]
      : []),
  ].filter((item) => {
    if (item.key === 'people.trm-candidates') {
      return (
        havePermissionForAppShell(CRM_PERMISSIONS.VIEW_CANDIDATE) &&
        trmEnabled &&
        isTrmAppShellEnabled
      );
    }

    return true;
  });

  return (
    <div
      className={classify(css.sideNavContainer, {[css.collapsed]: !expanded})}
    >
      <SideNavHeader expanded={expanded} setExpand={setExpand} />

      <div className={css.sideMenuLinkWrap}>
        {!trmEnabled || !isTrmAppShellEnabled ? (
          canViewAnalytics && (
            <SideMenuLink
              onChange={handleSideMenuChange}
              opened={expanded}
              pageNameKey="dashboard"
              customTitle={t('Dashboard', 'Dashboard')}
              selectedValue={selectedKey}
              to={getPathFromPageNameKey('dashboard')}
              linkComponent={
                Link as NonNullable<
                  React.ComponentProps<typeof SideMenuLink>['linkComponent']
                >
              }
              tooltip={{
                classNames: {tooltip: css.toolTip},
              }}
              classNames={{
                wrapper: classify({
                  [css.selectedDashboardLink]: selectedKey === 'dashboard',
                }),
              }}
            />
          )
        ) : crmMigrationEnabled ? (
          <SideMenuLink
            onChange={handleSideMenuChange}
            opened={expanded}
            pageNameKey="dashboard"
            customTitle={t('Dashboard', 'Dashboard')}
            selectedValue={selectedKey}
            to={getPathFromPageNameKey('dashboard')}
            linkComponent={
              Link as NonNullable<
                React.ComponentProps<typeof SideMenuLink>['linkComponent']
              >
            }
          />
        ) : (
          <SideMenuGroup
            sideMenuLinkKey="dashboard"
            customTitle={t('Dashboard', 'Dashboard')}
            subLinkOptions={
              filteredDashboardItems.map((item) => ({
                ...item,
              })) || []
            }
            selectedValue={selectedKey}
            expanded={expanded}
            onSubLinkSelect={handleSubLinkSelect}
            getPathFromPageNameKey={getPathFromPageNameKey}
          />
        )}

        {(canViewAnalytics || (!canViewAnalytics && canViewChat)) && (
          <SideMenuLink
            onChange={handleSideMenuChange}
            opened={expanded}
            pageNameKey="analytics"
            customTitle={t('Analytics_Data', 'Analytics & Data')}
            selectedValue={selectedKey}
            to={getPathFromPageNameKey('analytics')}
            linkComponent={
              Link as NonNullable<
                React.ComponentProps<typeof SideMenuLink>['linkComponent']
              >
            }
            tooltip={{
              classNames: {tooltip: css.toolTip},
            }}
            classNames={{
              wrapper: classify({
                [css.selectedDashboardLink]: selectedKey === 'analytics',
              }),
            }}
          />
        )}

        {automationSubMenus.length > 0 && (
          <SideMenuGroup
            sideMenuLinkKey="automation"
            customTitle={t('Automation', 'Automation')}
            subLinkOptions={automationSubMenus}
            selectedValue={selectedKey}
            expanded={expanded}
            onSubLinkSelect={handleSubLinkSelect}
            getPathFromPageNameKey={getPathFromPageNameKey}
          />
        )}
        {dataManagementSubMenus.length > 0 && (
          <SideMenuGroup
            sideMenuLinkKey="dataManagement"
            customTitle={t('Data_Management', 'Data Management')}
            subLinkOptions={dataManagementSubMenus}
            selectedValue={selectedKey}
            expanded={expanded}
            onSubLinkSelect={handleSubLinkSelect}
            getPathFromPageNameKey={getPathFromPageNameKey}
          />
        )}

        {filteredPeopleItems.length > 0 ? (
          <SideMenuGroup
            sideMenuLinkKey="people"
            customTitle={t('People', 'People')}
            subLinkOptions={filteredPeopleItems}
            selectedValue={selectedKey}
            expanded={expanded}
            onSubLinkSelect={handleSubLinkSelect}
            getPathFromPageNameKey={getPathFromPageNameKey}
          />
        ) : null}

        {trmEnabled && !isTrmAppShellEnabled && (
          <SideMenuLink
            onChange={handleSideMenuChange}
            opened={expanded}
            pageNameKey="trm"
            selectedValue={selectedKey}
            to={getPathFromPageNameKey('trm')}
            linkComponent={
              Link as NonNullable<
                React.ComponentProps<typeof SideMenuLink>['linkComponent']
              >
            }
            customTitle={t('TRM', 'TRM')}
            tooltip={{
              classNames: {tooltip: css.toolTip},
              placement: 'right',
            }}
            classNames={{
              wrapper: classify({
                [css.selectedDashboardLink]: selectedKey === 'trm',
              }),
            }}
          />
        )}

        {/* Enable before releasing to prod @swatantra */}
        {filteredJobsItems.length > 0 && (
          <SideMenuGroup
            sideMenuLinkKey="jobs"
            customTitle={t('Jobs', 'Jobs')}
            subLinkOptions={
              filteredJobsItems.map((item) => ({
                ...item,
              })) || []
            }
            selectedValue={selectedKey}
            expanded={expanded}
            onSubLinkSelect={handleSubLinkSelect}
            getPathFromPageNameKey={getPathFromPageNameKey}
          />
        )}

        {checkJdGeneratorEnabled() && (
          <SideMenuLink
            onChange={handleSideMenuChange}
            opened={expanded}
            customTitle={t('AI_Copilot', 'AI Copilot')}
            pageNameKey="sense-jd-ai"
            selectedValue={selectedKey}
            to={getPathFromPageNameKey('sense-jd-ai')}
            linkComponent={
              Link as NonNullable<
                React.ComponentProps<typeof SideMenuLink>['linkComponent']
              >
            }
            tooltip={{
              classNames: {tooltip: css.toolTip},
            }}
            classNames={{
              wrapper: classify({
                [css.selectedDashboardLink]: selectedKey === 'sense-jd-ai',
              }),
            }}
          />
        )}

        {sourcingSubMenus.length > 0 && (
          <SideMenuGroup
            sideMenuLinkKey="sourcing"
            customTitle={t('Sourcing', 'Sourcing')}
            subLinkOptions={sourcingSubMenus}
            selectedValue={selectedKey}
            expanded={expanded}
            onSubLinkSelect={handleSubLinkSelect}
            getPathFromPageNameKey={getPathFromPageNameKey}
          />
        )}

        {canViewChatorEditChatbot && (
          <SideMenuGroup
            sideMenuLinkKey="messaging"
            customTitle={t('Conversations', 'Conversations')}
            subLinkOptions={[
              {
                label: t('Messaging', 'Messaging'),

                key: 'messages',
              },
              ...(isHVBEnabledForMSG
                ? [
                    {
                      label: t('Broadcast', 'Broadcast'),
                      key: 'messages.hv-broadcasts',
                    },
                  ]
                : []),
              ...(hasQueueInbox && !disableSetOfMessagingProductsForGiGroup
                ? [
                    {
                      label: t('Live_Agent_Transfer', 'Live Agent Transfer'),
                      key: 'messages.queues',
                    },
                  ]
                : []),
              ...(!disableSetOfMessagingProductsForGiGroup
                ? [
                    {
                      key: 'contacts',
                      label: t('Messaging_Contacts', 'Messaging Contacts'),
                    },
                  ]
                : []),
              ...(canUseCrmMailbox
                ? [
                    {
                      key: 'mailbox',
                      label: t('Email', 'Email'),
                    },
                  ]
                : []),
            ]}
            selectedValue={selectedKey}
            expanded={expanded}
            onSubLinkSelect={handleSubLinkSelect}
            getPathFromPageNameKey={getPathFromPageNameKey}
            showStatusIndicator={showUnreadCountBadge}
            badgeTextDetails={{
              messages: showUnreadCountBadge ? msgUnreadCount : '',
            }}
          />
        )}

        <SideMenuLink
          onChange={handleSideMenuChange}
          opened={expanded}
          pageNameKey="configuration"
          customTitle={t('Configuration', 'Configuration')}
          selectedValue={selectedKey}
          data-qa-id="select-settings-page"
          to={getPathFromPageNameKey('configuration')}
          linkComponent={
            Link as NonNullable<
              React.ComponentProps<typeof SideMenuLink>['linkComponent']
            >
          }
          tooltip={{classNames: {tooltip: css.toolTip}}}
          classNames={{
            wrapper: classify({
              [css.selectedDashboardLink]: selectedKey === 'configuration',
            }),
          }}
        />
      </div>
      <div className={css.stickyBottom}>
        <AccountBar
          expanded={expanded}
          handleSenseUniversityOnClick={handleSenseUniversityOnClick}
        />
      </div>
    </div>
  );
}

SideNavBar.displayName = 'SideNavBar';
